import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

import { API, QUERY_KEYS } from '@config';
import { useRequest, useDisplayResponseMessage, useSetActiveProject, useMessage } from '@hooks';

const DROPDOWN_PAGE_SIZE = 25;

type ProjectListItem = {
  uuid: string;
  name: string;
};

type FlowListItem = {
  uuid: string;
  name: string;
  enabled: boolean;
  environment?: 'DEV' | 'TEST' | 'PROD';
};

export const useActiveProjectQuery = (projectUuid?: string) => {
  const request = useRequest();
  const displayResponseMessage = useDisplayResponseMessage();

  return useQuery({
    enabled: !!projectUuid,
    queryKey: [QUERY_KEYS.PROJECT_DETAIL, projectUuid],
    queryFn: async ({ signal }) => {
      try {
        const { data } = await request<ProjectListItem>(
          API.CORE_PROJECTS_RETRIEVE(projectUuid as string),
          { signal },
        );
        return data;
      } catch (error) {
        if (axios.isCancel(error)) throw error;
        displayResponseMessage('projects.fetch.error');
        throw error;
      }
    },
  });
};

type ListQueryParams = {
  search?: string;
  page?: number;
  limit?: number;
};

type PaginatedResponse<T> = {
  results: T[];
  total_count: number;
};

export const useProjectsListQuery = ({
  organizationUuid,
  search,
  page = 1,
  limit = DROPDOWN_PAGE_SIZE,
}: ListQueryParams & { organizationUuid?: string }) => {
  const request = useRequest();
  const displayResponseMessage = useDisplayResponseMessage();

  return useQuery({
    enabled: !!organizationUuid,
    keepPreviousData: true,
    queryKey: [QUERY_KEYS.PROJECTS_LIST, organizationUuid, search ?? '', page, limit],
    queryFn: async ({ signal }) => {
      try {
        const offset = (page - 1) * limit;
        const { data } = await request<PaginatedResponse<ProjectListItem>>(
          API.CORE_PROJECTS_LIST(organizationUuid as string),
          {
            params: { limit, offset, ...(search ? { search } : {}) },
            signal,
          },
        );
        return data;
      } catch (error) {
        if (axios.isCancel(error)) throw error;
        displayResponseMessage('projects.fetch.error');
        throw error;
      }
    },
  });
};

type FlowsListQueryParams = ListQueryParams & {
  projectUuid?: string;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  // NodeTypeResource identifier (slug, or name for legacy rows) — filters to
  // flows whose root node has this connector.
  sourceNodeType?: string;
  // Same identifier space; filters to flows with at least one leaf node of
  // this connector.
  targetNodeType?: string;
};

export const useFlowsListQuery = ({
  projectUuid,
  search,
  page = 1,
  limit = DROPDOWN_PAGE_SIZE,
  sortField,
  sortOrder,
  sourceNodeType,
  targetNodeType,
}: FlowsListQueryParams) => {
  const request = useRequest();
  const displayResponseMessage = useDisplayResponseMessage();

  return useQuery({
    enabled: !!projectUuid,
    keepPreviousData: true,
    queryKey: [
      QUERY_KEYS.FLOWS_LIST,
      projectUuid,
      search ?? '',
      page,
      limit,
      sortField ?? '',
      sortOrder ?? '',
      sourceNodeType ?? '',
      targetNodeType ?? '',
    ],
    queryFn: async ({ signal }) => {
      try {
        const offset = (page - 1) * limit;
        const { data } = await request<PaginatedResponse<FlowListItem>>(
          API.FLOWS_LIST(projectUuid as string),
          {
            params: {
              limit,
              offset,
              ...(search ? { search } : {}),
              ...(sortField ? { sort_by: sortField } : {}),
              ...(sortOrder ? { sort_order: sortOrder } : {}),
              ...(sourceNodeType ? { source_node_type: sourceNodeType } : {}),
              ...(targetNodeType ? { target_node_type: targetNodeType } : {}),
            },
            signal,
          },
        );
        return data;
      } catch (error) {
        if (axios.isCancel(error)) throw error;
        displayResponseMessage('flows.fetch.error');
        throw error;
      }
    },
  });
};

export const useCreateProjectMutation = (organizationUuid?: string) => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();
  const setActiveProject = useSetActiveProject();
  const message = useMessage();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      slug?: string;
      organization: string;
    }) => {
      // Only include slug if it has a value
      const payload: { name: string; organization: string; slug?: string } = {
        name: data.name,
        organization: data.organization,
      };
      if (data.slug && data.slug.trim()) {
        payload.slug = data.slug.trim();
      }

      const { data: project } = await request<Project>(API.CORE_PROJECTS_CREATE(), {
        method: 'POST',
        data: payload,
      });
      return project;
    },
    onSuccess: (project) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PROJECTS, organizationUuid],
      });
      setActiveProject(project.uuid);
      displayResponseMessage('projects.create.success');
    },
    onError: (error) => {
      console.error(error);

      // Handle specific error messages from backend
      if (error instanceof AxiosError && error.response?.data) {
        const errorData = error.response.data;

        // Check for permission error
        if (error.response.status === 403) {
          if (errorData.detail) {
            message.error(errorData.detail);
            return;
          }
          message.error("You don't have permission to create projects in this organization. Only owners and admins can create projects.");
          return;
        }

        // Check for validation errors (duplicate slug, etc.)
        if (error.response.status === 400) {
          // Handle field-specific errors
          if (errorData.slug) {
            const slugError = Array.isArray(errorData.slug) ? errorData.slug[0] : errorData.slug;
            message.error(slugError);
            return;
          }
          if (errorData.name) {
            const nameError = Array.isArray(errorData.name) ? errorData.name[0] : errorData.name;
            message.error(nameError);
            return;
          }
          // Handle non-field errors
          if (errorData.non_field_errors) {
            const nonFieldError = Array.isArray(errorData.non_field_errors)
              ? errorData.non_field_errors[0]
              : errorData.non_field_errors;
            message.error(nonFieldError);
            return;
          }
          if (errorData.detail) {
            message.error(errorData.detail);
            return;
          }
        }
      }

      // Fallback to generic error message
      displayResponseMessage('projects.create.error');
    },
  });
};
